"use strict";
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ CSV, read. C17.                                                           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **A PARSER IS RISKIER THAN A WRITER, AND THIS ONE IS HAND-WRITTEN ANYWAY.** Splitting on the
 * delimiter is the version everybody writes first and it is wrong the moment a task title contains a
 * comma — which the export itself produces, correctly quoted. The alternative was a dependency
 * (`csv-parse`); it is forty lines with the quoting rules written down, so it is here with a test per
 * rule instead. If a third quoting bug shows up, take the dependency.
 *
 * The rules, RFC 4180's: a field may be quoted; a quoted field may contain the delimiter, CR, LF and
 * doubled quotes; an unquoted field ends at the delimiter or the line ending.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.detectDelimiter = detectDelimiter;
exports.parseCsv = parseCsv;
/**
 * ⚠️⚠️ **THE DELIMITER IS DETECTED, AND THAT IS NOT A CONVENIENCE — IT IS THE ROUND TRIP.** This
 * product's export writes **commas**; the mockup's own import samples use **semicolons**; and Excel
 * with a Hungarian locale *saves* CSV with semicolons. So the obvious path — export, open in Excel,
 * fix a column, save, re-import — changes the delimiter under you, and a comma-only parser reads the
 * whole file as one column and reports every row as malformed.
 *
 * Decided on the HEADER line only, by which candidate splits it into more fields. A tie goes to the
 * comma, because that is what this product writes.
 */
function detectDelimiter(text) {
    const header = text.slice(0, text.search(/\r?\n/) === -1 ? text.length : text.search(/\r?\n/));
    const commas = splitLine(header, ',').length;
    const semis = splitLine(header, ';').length;
    return semis > commas ? ';' : ',';
}
/** One line, split on a delimiter, honouring quotes. Used by the detector and by the parser. */
function splitLine(line, delimiter) {
    const fields = [];
    let field = '';
    let quoted = false;
    for (let index = 0; index < line.length; index += 1) {
        const char = line[index];
        if (quoted) {
            if (char === '"') {
                /* A doubled quote is a literal one — RFC 4180, and the detail hand-rolled parsers miss. */
                if (line[index + 1] === '"') {
                    field += '"';
                    index += 1;
                }
                else {
                    quoted = false;
                }
            }
            else {
                /* ⚠️ `?? ''` because an index read is `string | undefined` to the compiler — lint refuses the
                 * bare `+`, and it is right: a silent `undefined` in a field would be the word "undefined". */
                field += char ?? '';
            }
            continue;
        }
        if (char === '"' && field === '') {
            quoted = true;
            continue;
        }
        if (char === delimiter) {
            fields.push(field);
            field = '';
            continue;
        }
        field += char ?? '';
    }
    fields.push(field);
    return fields;
}
/**
 * Parse a whole file.
 *
 * ⚠️ **The BOM is stripped**, because the export writes one and Excel writes one — and a first column
 * called `U+FEFF kulcs` matches nothing, which presents as *"the file has no key column"* over a file
 * that plainly does.
 *
 * ⚠️ **Header names are lower-cased and trimmed**, so `Kulcs ` and `kulcs` are the same column. Not
 * accent-folded: the export writes ASCII headers on purpose, and folding would make `ora` and `óra`
 * one column while leaving every other Hungarian word ambiguous.
 *
 * ⚠️ **A row with fewer fields than the header is not an error here.** Excel drops trailing empty
 * fields, so a row ending in three empty columns arrives short — treating that as malformed would
 * reject most real files. Missing fields read as empty strings, and the domain decides whether an
 * empty value is allowed.
 */
function parseCsv(text, delimiter) {
    const body = text.replace(/^\uFEFF/, '');
    const sep = delimiter ?? detectDelimiter(body);
    const lines = splitRecords(body);
    if (lines.length === 0)
        return { columns: [], rows: [] };
    const columns = splitLine(lines[0]?.text ?? '', sep).map((name) => name.trim().toLowerCase());
    const rows = lines.slice(1).flatMap((line) => {
        /* ⚠️ A blank line is skipped rather than reported: every file ends with one, and a trailing
         * newline is not a data error. */
        if (line.text.trim() === '')
            return [];
        const fields = splitLine(line.text, sep);
        const values = {};
        columns.forEach((name, index) => {
            values[name] = (fields[index] ?? '').trim();
        });
        return [{ lineNumber: line.lineNumber, values }];
    });
    return { columns, rows };
}
/**
 * Split into records, keeping a quoted newline inside its field.
 *
 * ⚠️⚠️ **`text.split('\n')` IS THE BUG THIS FUNCTION EXISTS FOR.** A worklog note with a line break in
 * it is written by the export as a quoted field containing a newline — perfectly legal CSV — and
 * splitting on the character turns one row into two, the second of which is garbage. Both halves then
 * appear in the error list, which is how a correct file gets reported as broken.
 */
function splitRecords(text) {
    const records = [];
    let current = '';
    let quoted = false;
    let lineNumber = 1;
    let startedAt = 1;
    for (let index = 0; index < text.length; index += 1) {
        const char = text[index];
        if (char === '"') {
            quoted = !quoted;
            current += char;
            continue;
        }
        if (!quoted && (char === '\n' || char === '\r')) {
            /* CRLF is one ending, not two. */
            if (char === '\r' && text[index + 1] === '\n')
                index += 1;
            records.push({ text: current, lineNumber: startedAt });
            current = '';
            lineNumber += 1;
            startedAt = lineNumber;
            continue;
        }
        if (quoted && (char === '\n' || char === '\r')) {
            /* ⚠️ Counted, so the NEXT record's line number is right even though this newline is data. */
            if (char === '\r' && text[index + 1] === '\n')
                index += 1;
            lineNumber += 1;
            current += '\n';
            continue;
        }
        current += char ?? '';
    }
    if (current !== '')
        records.push({ text: current, lineNumber: startedAt });
    return records;
}
//# sourceMappingURL=parse.js.map